import React, { useContext, useMemo, useState } from 'react'

import { connect } from 'react-redux'
import { getTranslate } from 'react-localize-redux'
import { Button, Modal, Switch, Tooltip } from 'antd'
import { getUser } from '../../reducers/UserReducer'
import { faEdit, faStar } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { InstitutionOptions } from '../../utils/constants'
import OptionValueModal from './OptionValueModal'
import { InstitutionContext } from '../../Providers/InstitutionProvider'
import { updateInstitutionOption } from '../../utils/api/institution'
import { onError, onSuccess } from '../../utils/apiHelper'

const MODAL_TITLE = "Update option's value"
const P_STYLE = { margin: '0px', display: 'flex', flexDirection: 'row', alignItems: 'center' }
const OPTIONS_WITH_DAY_VALUES = [InstitutionOptions.AUTO_ACCEPTATION_DAYS, InstitutionOptions.STUDENT_DESIDERATA_LIMIT]

const mapStateToProps = state => ({
  t: getTranslate(state.locale),
  user: getUser(state.getUser)
})

export const STRING_FIELD = 0
export const BOOLEAN_FIELD = 1

const InstitutionOptionTable = ({ columns, density, user, t }) => {
  const { institutionOptions, loading, updateOption } = useContext(InstitutionContext)

  const [optionToUpdate, setOptionToUpdate] = useState(null)
  const [warningModal, setWarningModal] = useState({ visible: false, text: '' })

  const optionsTree = useMemo(() => {
    const optionsTree = {}

    institutionOptions.forEach(o => {
      const body = {
        ...o,
        type: o.optionType.type,
        isEditable: o.optionType.isEditable,
        isHidden: o.optionType.isHidden,
        isPremium: o.optionType.isPremium,
        children: [],
        parent: o.optionType.parent?.id ?? null
      }

      o.optionType.children.forEach(c => {
        body.children.push(c.id)
      })

      optionsTree[o.optionType.id] = body
    })

    return optionsTree
  }, [institutionOptions])

  const options = useMemo(() => {
    const options = { parents: {}, data: [] }

    Object.keys(optionsTree).forEach(key => {
      const o = optionsTree[key]

      if (o.parent !== null) {
        options.parents[key] = false

        if (optionsTree[o.parent] !== undefined) {
          options.parents[key] = optionsTree[o.parent].optionEnabled
        }
      }

      if (!o.isHidden && (options.parents[key] || options.parents[key] === undefined)) {
        if (o.isPremium && !o.isEditable) {
          if (o.optionEnabled) {
            options.data.push(o)
          }
        } else {
          options.data.push(o)
        }
      }
    })

    return options
  })

  const renderHeadDetails = () => {
    return columns.map((column, index) => {
      if (index === 0) {
        return (
          <th key={index} className='name-header-institution-option'>
            {t(column.name)}
          </th>
        )
      } else {
        return (
          <th key={index} className='switch-header-institution-option'>
            {t(column.name)}
          </th>
        )
      }
    })
  }

  const renderName = data => {
    return (
      <td key={`col_name_${data.id}`} className='name_institution_option'>
        <div className='flex-row' style={{ margin: '5px' }}>
          {OPTIONS_WITH_DAY_VALUES.includes(data.type) && data.optionEnabled && (
            <Button onClick={() => setOptionToUpdate(data)} style={{ marginRight: '5px' }}>
              <FontAwesomeIcon icon={faEdit} />
            </Button>
          )}
          <Tooltip placement='top' title={t('institution.option.' + data.type)}>
            <p style={P_STYLE}>
              {data.isPremium && (
                <Tooltip className='mr-4' placement='top' title={t('Premium feature')}>
                  <FontAwesomeIcon icon={faStar} style={{ color: '#03dbfc' }} />
                </Tooltip>
              )}
              {t('institution.option.' + data.type)}
            </p>
          </Tooltip>
        </div>
      </td>
    )
  }

  const renderSwitch = data => {
    return (
      <td key={`col_institution_option${data.id}`} className='switch-institution-option'>
        <Tooltip
          placement='top'
          title={data.optionEnabled ? t('Enabled') : t('Disabled')}
        >
          <Switch
            checked={data.optionEnabled}
            size='small'
            onChange={e => updateDataValue(data, e)}
            disabled={
              loading || !data.isEditable
            }
          />
        </Tooltip>
      </td>
    )
  }

  const patchOption = (option) => {
    updateInstitutionOption(user, option, { optionEnabled: option.optionEnabled, optionValue: option.value }).then(({ data }) => {
      updateOption(data)
      onSuccess(t('institution_option.update.success'))
    }).catch(() => {
      onError(t('institution_option.update.error'))
    })
  }

  const updateDataValue = (data, e) => {
    if (data.type === InstitutionOptions.DRAFT_INTERNSHIPS && !e) {
      setWarningModal({
        visible: true,
        text: 'If you deactivate the draft internship requests functionnality, all the pre-accepted internships will be revert back to created internships.',
        callback: () => {
          setWarningModal({ visible: false, text: '' })
          patchOption({ ...data, optionEnabled: e })
        }
      })
    } else {
      patchOption({ ...data, optionEnabled: e })
    }
  }

  const handleOptionValueUpdate = value => {
    setOptionToUpdate(null)
    patchOption({ ...optionToUpdate, optionValue: value })
  }

  return (
    <div className='interactive-table'>
      {loading && (
        <div className='center'>
          <div className='loading-icon black' />
        </div>
      )}
      {!loading && options.data.length > 0 && (
        <table style={{ width: '100%' }}>
          <thead>
            <tr>
              {columns && renderHeadDetails()}
            </tr>
          </thead>
          <tbody>
            {columns && options.data.map((data, index) => {
              return (
                <tr key={'row_' + index} style={{ height: density ?? '32px' }}>
                  {columns.map(c => {
                    let cell = null

                    if (c.type === STRING_FIELD) {
                      cell = renderName(data, index)
                    } else if (c.type === BOOLEAN_FIELD) {
                      cell = renderSwitch(data, index)
                    } else {
                      cell = (
                        <td key={`col_${data.id}`} className='name_institution_option'>
                          {data.optionValue} {OPTIONS_WITH_DAY_VALUES.includes(data.type) && (' ' + t('days'))}
                        </td>
                      )
                    }

                    return cell
                  })}
                </tr>
              )
            })}
            {!columns && (
              <tr> {t('No columns have been found')} </tr>
            )}
          </tbody>
        </table>
      )}
      {!(loading || options.data.length > 0) && (
        <span> {t('No data is currently available for this page.')} </span>
      )}
      <OptionValueModal
        option={optionToUpdate}
        title={t(MODAL_TITLE)}
        onCancel={() => setOptionToUpdate(null)}
        onOk={handleOptionValueUpdate}
      />
      <Modal
        visible={warningModal.visible}
        title={t('Module activation/deactivation warning')}
        onOk={() => warningModal.callback()}
        onCancel={() => setWarningModal({ visible: false, text: '' })}
      >
        {t(warningModal.text)}
      </Modal>
    </div>
  )
}

export default connect(mapStateToProps)(InstitutionOptionTable)
